import React, { createContext, useContext } from 'react';
import { ComposedConfig } from '~types';
import { AuthToken } from '~types/auth';
import { ServicesOfferConfigResponse } from '~types/sfc';

/**
 * Temporary solution until we have a tracking as a shared lib
 */

type TrackLinkEventProps = {
    trackLinkEvent?: boolean;
    linkCallBack?: (...args: any[]) => any;
};
type TrackParams = Record<string, unknown>;
export type TrackEventFn = (
    name?: string,
    params?: TrackParams,
    touchPointButton?: string,
    trackLinkEventProps?: TrackLinkEventProps
) => void;

type TrackPageViewFn = (
    name?: string,
    params?: TrackParams,
    component?: React.ComponentType<any>,
    forceTracking?: boolean,
    setTouchPoint?: boolean
) => void;

export type PWATracker = {
    trackPageView: TrackPageViewFn;
    trackEvent: TrackEventFn;
    setTouchPointPage: (page: string | null | undefined) => void;
    getTouchPointPage: () => string | null;
};

export type PWADispatchToastMessage = (
    message: string | React.ReactElement,
    displayTime?: number,
    position?: string
) => void;

export type PWAutilsType = {
    redirectToLogin?: (
        backUrl?: string | undefined,
        prompt?: 'none' | undefined
    ) => void;
    dispatchToastMessage?: PWADispatchToastMessage;
    trackingParams?: Record<string, string>;
};

export type SFCUtilsType = {
    useServicesOfferConfig: (
        offerID: number,
        shouldFetch?: boolean
    ) => ServicesOfferConfigResponse;
};

export type GetExistingConversationId = (
    adId: string,
    buyerId?: string
) => Promise<string | null>;

export type AppContextType = {
    adId: number;
    routerBasePath?: string;
    appConfig: ComposedConfig;
    PWAutils: PWAutilsType;
    getAuthToken: () => Promise<AuthToken | undefined>;
    tracker: PWATracker;
    authenticated: boolean;
    sendHotjarEvent?: (name: string) => void;
    SFCUtils: SFCUtilsType;
};

const AppContext = createContext<AppContextType | undefined>(undefined);

type CDBContextProviderProps = {
    children: React.ReactNode;
    value: AppContextType;
};

const AppContextProvider = ({ children, value }: CDBContextProviderProps) => {
    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

const useAppContext = () => {
    const context = useContext(AppContext);

    if (context === undefined) {
        throw new Error('useAppContext must be within AppContextProvider');
    }

    return context;
};

export { AppContextProvider, useAppContext };
